<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { API, HOST_TULUM } from '@/utils/http'
import { formatTime } from '@/utils/datetime'
import { getBaseUrl } from '@/utils/url'
import { toast } from '@/components/ui/toast'
import { Spinner } from '@/components/ui/spinner'
import { Badge } from '@/components/ui/badge'
import { Eye, XCircle } from 'lucide-vue-next'

const route = useRoute()
const store = useStore()

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    required: true
  }
})

const orgId = computed(() => route.params.org_id)
const settings = computed(() => store.getters['user/getUserSettings'])
const itemsData = ref([])
const isLoading = ref(false)
const error = ref(null)
const visibleItems = ref({})

const host = getBaseUrl()

// Order status from the layer - it's in the layer.layer object for order type layers
const orderStatus = computed(() => {
  // For order type layers, status is in layer.properties.layer.status
  return props.layer?.properties?.layer?.status ?? 0
})

const isOrderComplete = computed(() => {
  return orderStatus.value === 0
})

const isOrderProcessing = computed(() => {
  return orderStatus.value > 0
})

const isOrderFailed = computed(() => {
  return orderStatus.value < 0
})

const statusToLabel = {
  '4': 'Creating tiles',
  '3': 'Waiting for result',
  '2': 'Activating',
  '1': 'Initializing',
  '0': 'Done',
  '-1': 'Failed: Initialization',
  '-2': 'Failed: Activation',
  '-3': 'Failed: Waiting for result',
  '-4': 'Failed: Creating tiles',
}

const item_ids = computed(() => {
  return props.layer?.properties?.layer?.data?.item_ids || []
})

const products = computed(() => {
  // Check for new multi-product format
  if (props.layer?.properties?.layer?.data?.products) {
    return props.layer.properties.layer.data.products
  }
  // Fall back to legacy single product format
  if (props.layer?.properties?.layer?.data?.item_ids) {
    return [{
      item_ids: props.layer.properties.layer.data.item_ids,
      item_type: props.layer.properties.layer.data.item_type || 'PSScene',
      product_bundle: props.layer.properties.layer.data.product_bundle || 'visual'
    }]
  }
  return []
})

const planetOrderId = computed(() => {
  return props.layer?.properties?.layer?.planet_order_id
})

const product_bundle = computed(() => {
  return props.layer?.properties?.layer?.data?.product_bundle || 'visual'
})

const hasItemData = computed(() => {
  return products.value.length > 0 && products.value.some(p => p.item_ids?.length > 0)
})

const map = computed(() => store.getters['geo/getMap'])

const toggleVisibility = (itemId) => {
  visibleItems.value[itemId] = !visibleItems.value[itemId]
}

const fetchItemsData = async () => {
  // Only fetch if order is complete
  if (!products.value.length || !orgId.value || !isOrderComplete.value) {
    return
  }

  isLoading.value = true
  error.value = null
  itemsData.value = []

  try {
    // Collect all API calls to run in parallel
    const apiCalls = []

    // Prepare API calls for each product's items
    for (const product of products.value) {
      if (!product.item_ids?.length) continue

      for (const itemId of product.item_ids) {
        const apiCall = API({
          method: 'GET',
          url: `${HOST_TULUM}/search/item/${orgId.value}/${product.item_type}/${itemId}`
        }).then(response => ({
          ...response.data,
          product_bundle: product.product_bundle,
          item_type: product.item_type
        })).catch(err => {
          const errorMsg = err.response?.data?.detail || err.message || 'Network error'
          toast({
            title: 'Item Fetch Error',
            description: `Failed to load item ${itemId}: ${errorMsg}`,
            status: 'error',
          })
          // Return null for failed requests to filter out later
          return null
        })

        apiCalls.push(apiCall)
      }
    }

    // Execute all API calls in parallel
    const results = await Promise.all(apiCalls)

    // Filter out failed requests (null values) and set the data
    itemsData.value = results.filter(item => item !== null)
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to fetch items data'
  } finally {
    isLoading.value = false
  }
}

// Watch for changes and fetch data when component mounts or products change
// Only fetch if order is complete
watch([products, orgId, orderStatus], () => {
  if (products.value.length && orgId.value) {
    // Only fetch if order is complete
    if (isOrderComplete.value) {
      fetchItemsData()
    }
  }
}, { immediate: true })

onMounted(() => {
  if (products.value.length && orgId.value && isOrderComplete.value) {
    fetchItemsData()
  }
})
</script>

<template>
  <div>
    <!-- Data Tab Content -->
    <div v-if="activeTab === 'data'">
      <div class="space-y-4">
        <!-- Order Status (Processing/Failed) -->
        <div v-if="isOrderProcessing" class="flex items-center py-4">
          <Spinner class="w-5 h-5 mr-2" />
          <div>
            <h4 class="font-medium">Order Processing</h4>
            <p class="text-sm text-muted-foreground">{{ statusToLabel[orderStatus] }}</p>
          </div>
        </div>

        <div v-else-if="isOrderFailed" class="flex items-center py-4">
          <XCircle class="w-5 h-5 mr-2 text-red-500" />
          <div>
            <h4 class="font-medium text-red-800">Order Failed</h4>
            <p class="text-sm text-red-600">{{ statusToLabel[orderStatus] }}</p>
          </div>
        </div>

        <!-- Loading state for completed orders -->
        <div v-else-if="isLoading && isOrderComplete" class="flex items-center justify-center py-8">
          <Spinner class="w-6 h-6 mr-2" />
          <span class="text-sm text-muted-foreground">Loading item data...</span>
        </div>

        <div v-else-if="error && isOrderComplete" class="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 class="font-medium text-red-800 mb-2">Error loading item data</h4>
          <p class="text-sm text-red-600">{{ error?.message }}</p>
        </div>

        <!-- Show items only when order is complete -->
        <div v-else-if="itemsData.length > 0 && isOrderComplete" class="space-y-3">
          <!-- Scene Items in Order Style -->
          <div
            v-for="(item, index) in itemsData"
            :key="item.id || index"
            class="mb-3 rounded border border-gray-200 p-2"
          >
            <div class="flex space-x-3">
              <!-- Thumbnail with Eye Icon -->
              <div class="relative">
                <button
                  type="button"
                  class="min-w-32 min-h-32 w-32 h-32 border rounded cursor-pointer relative"
                  :class="{ 'border-primary': visibleItems[item.id || index] }"
                  @click="toggleVisibility(item.id || index)"
                >
                  <div class="absolute top-0.5 left-0.5 flex items-center space-x-1 bg-gray-300 rounded p-1">
                    <Eye class="w-4.5 h-4.5" />
                  </div>
                  <img
                    v-if="item.id"
                    :src="`${host}/tulum/search/thumb/${orgId}/${item.properties?.item_type || 'PSScene'}/${item.id}?width=256`"
                    alt="Scene preview"
                    class="w-full h-full object-cover rounded bg-gray-100 p-1.5"
                  />
                  <div v-else class="w-full h-full rounded bg-gray-100" />
                </button>
              </div>

              <!-- Scene Details -->
              <div class="flex-1">
                <h2 class="font-bold mb-1">{{ formatTime(item.properties?.acquired, settings.tz, 'D MMMM YYYY') }}</h2>
                <div>
                  <p class="flex justify-between">
                    <span>Type:</span>
                    <span>{{ item.properties?.item_type }}</span>
                  </p>
                  <p class="flex justify-between">
                    <span>Cloud Cover:</span>
                    <span>{{ (item.properties?.cloud_cover || 0).toFixed(2) }}%</span>
                  </p>
                  <p class="flex justify-between">
                    <span>Clear Confidence:</span>
                    <span>{{ item.properties?.clear_confidence_percent || 0 }}%</span>
                  </p>
                  <p class="flex justify-between">
                    <span>Pixel Resolution:</span>
                    <span>{{ item.properties?.pixel_resolution }}m/px</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Show message if no items and order is complete -->
      <p v-if="!hasItemData && isOrderComplete" class="text-sm text-muted-foreground">No Planet Order item data available</p>
    </div>

    <!-- Filter Tab Content -->
    <div v-if="activeTab === 'filter'">
      <p class="text-sm text-muted-foreground">Planet Order filters will be available here</p>
    </div>
  </div>
</template>
